// "No organizations" stranded-signup page — task 104d.1 D1.7.
//
// Public URL: /no-organizations
//
// Reached by a Clerk-authenticated user who has no active Memberships. The
// redirect is enforced by `ClerkSync` (see context/ClerkSync.tsx) — this
// page is the destination that was previously an inline component. Moving
// it to its own route gives it a bookmarkable URL and lets us run axe over
// the landing page in isolation.
//
// Copy follows the "professional's assistant, not surveillance" framing:
// we explain the situation and offer two paths (wait for an invite, or
// sign out). We don't imply they're being watched.

import { SignOutButton } from '@clerk/clerk-react';
import { KanjoyWordmark } from '@/components/primitives/KanjoyWordmark';
import { Button } from '@/components/primitives/Button';
import { config } from '@/config';
import { useAppContext } from '@/context/AppContext';
import styles from './NoOrgsPage.module.css';

export function NoOrgsPage() {
  return (
    <div className={styles.page}>
      <header className={styles.brandHeader}>
        <KanjoyWordmark className={styles.wordmark} variant="dark" />
        <p className={styles.tagline}>Time tracking that doesn{'’'}t feel like time tracking.</p>
      </header>
      <main className={styles.card}>
        <h1 className={styles.title}>You{'’'}re signed in, but not in any organization yet</h1>
        <p className={styles.lede}>
          Kanjoy memberships are per-organization. If someone from your firm invited you, check your
          email for the invite link. Otherwise, reach out to your admin so they can add you.
        </p>
        <div className={styles.actions}>
          <SignOutCta />
        </div>
      </main>
      <footer className={styles.footer}>
        <p className={styles.footnote}>
          Kanjoy captures your billable time quietly, so you can review a draft instead of
          reconstructing one.
        </p>
      </footer>
    </div>
  );
}

/**
 * Sign-out CTA. In live mode we use Clerk's `<SignOutButton>` (the canonical
 * integration). In mockup mode we fall back to the AppContext's `signOut`
 * callback so the button is clickable without a ClerkProvider overhead.
 */
function SignOutCta() {
  const { signOut } = useAppContext();
  if (config.mockupMode) {
    return (
      <Button variant="secondary" onClick={signOut}>
        Sign out
      </Button>
    );
  }
  return (
    <SignOutButton>
      <Button variant="secondary">Sign out</Button>
    </SignOutButton>
  );
}
